/**
 * Atlas of awarenesses — the player's collection of unique floating phrases
 * with sighting counts.
 *
 * Doc 28 §3 (P0 #2): we track how many times each phrase has appeared
 * in a tap. `.has(id)` still answers «is it collected?» (true after the
 * first sighting), but now we can also surface «появлялась N раз», sort
 * by rarity, and weight phrase selection to favor under-seen ones.
 *
 * Adding a phrase still reports `wasNew=true` only on the first sighting —
 * subsequent calls just increment the counter.
 *
 * Atlas completeness per epoch unlocks a permanent multiplier (`docs/09`
 * §III.3) — that logic is membership-based and unchanged.
 */
import type { Epoch, PhraseEntry } from './types.js';

export interface AddToAtlasResult {
  readonly atlas: ReadonlyMap<string, number>;
  readonly wasNew: boolean;
}

/**
 * Record a phrase sighting. Returns a NEW Map — never mutates input.
 *
 * `wasNew` is true only the first time the phrase is added (count was 0).
 * Lesson-unlock and milestone-unlock pipelines depend on this signal, so
 * subsequent sightings of the same phrase must NOT report `wasNew=true`.
 */
export function addToAtlas(atlas: ReadonlyMap<string, number>, phraseId: string): AddToAtlasResult {
  const prev = atlas.get(phraseId) ?? 0;
  const next = new Map(atlas);
  next.set(phraseId, prev + 1);
  return { atlas: next, wasNew: prev === 0 };
}

export interface AtlasEpochProgress {
  readonly epoch: Epoch;
  readonly collected: number;
  readonly total: number;
  readonly complete: boolean;
}

/**
 * For each epoch, count how many of its phrases the player has collected.
 * Pure function over current state and the full phrase catalog.
 *
 * Uses membership (`.has`), so count > 0 = collected = 1 toward this number.
 */
export function computeAtlasProgress(
  atlas: ReadonlyMap<string, number>,
  allPhrases: readonly PhraseEntry[],
): readonly AtlasEpochProgress[] {
  const byEpoch = new Map<Epoch, { collected: number; total: number }>();
  for (const phrase of allPhrases) {
    const bucket = byEpoch.get(phrase.epoch) ?? { collected: 0, total: 0 };
    bucket.total += 1;
    if (atlas.has(phrase.id)) {
      bucket.collected += 1;
    }
    byEpoch.set(phrase.epoch, bucket);
  }

  const out: AtlasEpochProgress[] = [];
  for (const [epoch, bucket] of byEpoch.entries()) {
    out.push({
      epoch,
      collected: bucket.collected,
      total: bucket.total,
      complete: bucket.total > 0 && bucket.collected === bucket.total,
    });
  }
  out.sort((a, b) => a.epoch - b.epoch);
  return out;
}

/**
 * Compute the atlas-derived bonus (the "+X%" component fed into the global
 * multiplier). Each fully-collected epoch adds 5 % (per `docs/09` §III.3).
 */
export function computeAtlasBonus(progress: readonly AtlasEpochProgress[]): number {
  let bonus = 0;
  for (const p of progress) {
    if (p.complete) {
      bonus += 0.05;
    }
  }
  return bonus;
}

/**
 * How many times the player has seen a specific phrase. 0 if never seen.
 *
 * Convenience accessor for UI («появлялась N раз» badge) and for
 * anti-repeat weighting in phrase selection.
 */
export function getPhraseSightingCount(
  atlas: ReadonlyMap<string, number>,
  phraseId: string,
): number {
  return atlas.get(phraseId) ?? 0;
}
